import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import getFilterValues from 'api/getFilterValues';
import GqlClientContext from 'contexts/GqlClientContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import ToastContext from 'contexts/ToastContext';
import captureException from 'services/captureException';
import appRoutes, {
  buildShowEntityApp,
  buildShowEntityAppContentTab,
} from 'navigation/appRoutes';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import useSelectedEntity from './useSelectedEntity';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { CurrentUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/CurrentUserContentSettingsProvider';
import portalTypeCheckers from '../../../types/portalTypeCheckers';
import { getMappedEntityLinkId } from '../../EngagementPortalUsers/PortalMappedUsers';

type GetFirstEntityArgs = {
  field: string;
  filterInput?: FilterInput[];
  dateScope: DateRangeInput;
  client: ApolloClient<NormalizedCacheObject>;
  accountId: string;
  value: string;
};

type GetFirstEntityResponse =
  | {
      isUnfilteredMode: true;
      firstEntityValue: undefined;
      entity: EntityDetails.Entity;
      entityApp: EntityDetails.App;
      selectedPortal: Portal | undefined;
    }
  | {
      isUnfilteredMode: false;
      firstEntityValue: string;
      entity: EntityDetails.Entity;
      entityApp: EntityDetails.App;
      selectedPortal: Portal | undefined;
    };

const useEntityDetailsRedirectLink = () => {
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext);
  const { client } = useContext(GqlClientContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const selectedEntityContent = useSelectedEntity();

  const [args, setArgs] = useState<GetFirstEntityArgs | undefined>();
  const [redirectLink, setRedirectLink] = useState<string | undefined>();

  const buildArgs = useCallback((): GetFirstEntityArgs | undefined => {
    if (!selectedEntityContent) {
      return undefined;
    }
    const { entity } = selectedEntityContent;

    const filterInput: FilterInput[] = [
      { dataType: [entity.entityDataset] },
      entity.isActiveFilter,
    ];

    return {
      client,
      field: entity.primaryField,
      filterInput,
      dateScope: {},
      accountId: selectedAccountId,
      value: '',
    };
  }, [client, selectedAccountId, selectedEntityContent]);
  useEffect(() => {
    setArgs(buildArgs());
  }, [buildArgs]);

  const getFirstEntityValue = useCallback(
    async (
      args: GetFirstEntityArgs,
    ): Promise<GetFirstEntityResponse | undefined> => {
      if (!selectedEntityContent) {
        return;
      }
      const { entity, app } = selectedEntityContent;

      if (
        contentSettings.mode === 'normal' &&
        portalTypeCheckers.isEngagementPortal(selectedPortal)
      ) {
        const mappedEntityLink = getMappedEntityLinkId(
          selectedPortal,
          entity.id,
        );
        if (mappedEntityLink) {
          if (mappedEntityLink.entityId === entity.id) {
            return {
              isUnfilteredMode: true,
              firstEntityValue: undefined,
              entity,
              entityApp: app,
              selectedPortal,
            };
          }
        }
      }

      return getFilterValues(args).then((response) => {
        if (response.length === 0) {
          const e = new Error();
          e.name = `EntityDetailsShowRedirect: No entity value found (${entity.name})`;
          captureException(e);
          navigate(appRoutes.home);
          showToast(`We couldn't find any ${entity.name}s`);
          return undefined;
        } else {
          return {
            isUnfilteredMode: false,
            firstEntityValue: response[0],
            entity,
            entityApp: app,
            selectedPortal,
          };
        }
      });
    },
    [
      contentSettings.mode,
      navigate,
      selectedEntityContent,
      selectedPortal,
      showToast,
    ],
  );

  const handleResponse = useCallback(
    (response: GetFirstEntityResponse | undefined) => {
      if (!response) {
        return;
      }

      const newLink: string = (() => {
        if (response.entityApp.tabs.length === 0) {
          return buildShowEntityApp({
            entityApp: response.entityApp,
            value: response.firstEntityValue,
            portal: response.selectedPortal,
          });
        } else {
          return buildShowEntityAppContentTab({
            entityApp: response.entityApp,
            value: response.firstEntityValue,
            tabId: response.entityApp.tabs[0].typeId,
            tabType: response.entityApp.tabs[0].type,
            portal: response.selectedPortal,
          });
        }
      })();

      setRedirectLink(newLink);
    },
    [],
  );

  useLockedDebouncedEffect({
    args,
    responseHandler: handleResponse,
    callback: getFirstEntityValue,
  });

  return redirectLink;
};

export default useEntityDetailsRedirectLink;
